import {
  AppBar,
  Button,
  CircularProgress,
  Hidden,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme, makeStyles, Theme } from '@material-ui/core/styles';
import { Menu } from '@material-ui/icons';
import { Language } from '@material-ui/icons';
import HelpIcon from '@material-ui/icons/HelpOutline';
import clsx from 'clsx';
import PopupState, { bindPopover, bindToggle } from 'material-ui-popup-state';
import { useRouter } from 'next/router';
import { FC, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import LineInfo from '../../components/line-info';
import UserInfo from '../../components/user-info';
import { VersionContext } from '../../contexts/version';
import { properties as navigationProperties } from '../../layout/navigation';
import Link from '../../link';

export const FIXED_HEADER_BREAKPOINT = 'md';

const useStyles = makeStyles((theme: Theme) => ({
  // TODO: Factor this to supply a common interface with Anchor/Link.
  factorFakeAnchor: {
    color: '#FFB200',
    '&:hover *': {
      color: '#FFB200 !important',
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  bar: {
    backgroundColor: theme.palette.primary.main,
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  barShifted: {
    marginLeft: navigationProperties.width,
    width: `calc(100% - ${navigationProperties.width}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  logo: {
    display: 'inline-block',
    [theme.breakpoints.up('sm')]: {
      height: 40,
    },
    [theme.breakpoints.down('xs')]: {
      height: 26,
    },
    paddingLeft: 16,
    verticalAlign: 'middle',
    '& > svg': {
      height: '100%',
    },
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  title: {
    flex: 1,
    textOverflow: 'clip',
  },
  menuButton: {
    marginLeft: 12,
    [theme.breakpoints.down('sm')]: {
      marginLeft: 8,
    },
    transition: theme.transitions.create(['width', 'margin', 'padding'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflow: 'hidden',
  },
  hideSmooth: {
    width: 0,
    margin: 0,
    padding: 0,
  },
  hide: {
    display: 'none',
  },
  rightsideGuttersSmall: {
    paddingRight: 8,
  },
  rightsideGuttersLarge: {
    paddingRight: 16,
  },
  loggedInInfo: {
    lineHeight: '100%',
    textAlign: 'right',
  },
  loggedInEmail: {
    fontSize: '10px',
  },
  stagingStriped: {
    backgroundImage: `repeating-linear-gradient(-45deg, ${theme.palette.primary.main}, ${theme.palette.primary.main} 10px, rgba(255, 255, 255, 0.075) 10px, rgba(255, 255, 255, 0.075) 20px)`,
  },
  verticalAlignMiddle: {
    verticalAlign: 'middle',
  },
  language: {
    marginRight: '5px',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  text: {
    color: theme.palette.getContrastText(theme.palette.primary.main),
    textTransform: 'uppercase',
  },
  rightPadding: {
    paddingRight: '1em',
    [theme.breakpoints.down('sm')]: {
      paddingRight: '0.5em',
    },
  },
  changeLanguageProgress: {
    color: '#ffb200',
    position: 'absolute',
    top: '-2px',
    left: '-2px',
    zIndex: 1,
  },
  wrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  lineDescription: {
    fontSize: '10px',
  },
  helpIcon: {
    color: 'white',
    '&:hover *': {
      color: '#FFB200 !important',
    },
  },
  buttonPadding: {
    [theme.breakpoints.down('sm')]: {
      padding: 5,
    },
  },
}));

interface Properties {
  isAuthPage?: boolean;
  isNavigationOpen?: boolean;
  openNavigation?: any;
  availableLanguages?: {
    [key: string]: { nativeName: string };
  };
  lineDescription?: string;
  dashboardMenu?: React.ReactNode | null;
}

const Header: FC<Properties> = ({
  openNavigation,
  isNavigationOpen,
  isAuthPage,
  dashboardMenu,
  availableLanguages = { 'en-GB': { nativeName: 'English' } },
}) => {
  const router = useRouter();
  const [isLoadingLanguage, setLoadingLanguage] = useState(false);
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const { commit, version } = useContext(VersionContext);

  const handleChangeLanguage = useCallback(
    (languageCode: any, cb: any) => {
      setLoadingLanguage(true);

      if (i18n) {
        // In the newest i18n version use promise instead of callback.
        i18n.changeLanguage(languageCode, (err: any, _t: any) => {
          setLoadingLanguage(false);

          // Force reload to make sure language change takes effect.
          window.location.reload();

          if (err) {
            console.error(`Error changing language: ${err}`);
          }
        });
      }

      cb();
    },
    [i18n],
  );

  const handleLangClick = useCallback((lang: any, cb: any) => {
    return () => handleChangeLanguage(lang, cb);
  }, []);

  const userInfo = !isAuthPage ? (
    <Hidden smDown>
      <UserInfo className={classes.rightPadding} alignRight />
    </Hidden>
  ) : null;

  const lineInfo = !isAuthPage ? <LineInfo className={classes.rightPadding} t={t} alignRight /> : null;

  const { pathname } = router;

  const currentLanguage = (() => {
    const currentLanguage = (i18n && i18n.language) || 'en-GB';

    return availableLanguages[currentLanguage] ? currentLanguage : 'en-GB';
  })();

  const languagePicker = (
    <PopupState variant={'popover'} popupId={'languagePicker'}>
      {(popupState: any) => (
        <>
          <Button className={clsx(classes.factorFakeAnchor, classes.buttonPadding)} {...bindToggle(popupState)}>
            <Typography className={classes.text} variant={'body2'}>
              <span className={clsx(classes.language, classes.verticalAlignMiddle)}>
                {availableLanguages[currentLanguage].nativeName}
              </span>
              <span className={clsx('rtl-padding-right', classes.wrapper)}>
                <Language className={classes.verticalAlignMiddle} />
                {isLoadingLanguage && (
                  <CircularProgress size={28} thickness={3} className={classes.changeLanguageProgress} />
                )}
              </span>
            </Typography>
          </Button>
          <Popover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Paper>
              <MenuList>
                {Object.keys(availableLanguages).map((lang) => {
                  return (
                    <MenuItem key={lang} onClick={handleLangClick(lang, popupState.close)}>
                      {availableLanguages[lang].nativeName}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Paper>
          </Popover>
        </>
      )}
    </PopupState>
  );

  const navigationButton = !isAuthPage ? (
    <IconButton
      aria-label="Menu"
      onClick={openNavigation}
      className={clsx(
        classes.menuButton,
        classes.text,
        classes.factorFakeAnchor,
        isNavigationOpen && classes.hideSmooth,
      )}
    >
      <Menu />
    </IconButton>
  ) : null;

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isDashboardPage = pathname?.startsWith('/dashboard');

  const helpIcon = !isAuthPage ? (
    <IconButton aria-label="help" href="/help" className={classes.helpIcon}>
      <HelpIcon />
    </IconButton>
  ) : null;

  const isStaging = !!(version && !/^v\d+\.\d+\.\d+$/.test(version));

  return (
    <AppBar
      color="primary"
      className={clsx(classes.bar, isNavigationOpen && classes.barShifted, { [classes.stagingStriped]: isStaging })}
      position={smallScreen || isDashboardPage ? 'absolute' : 'fixed'}
    >
      <Toolbar
        disableGutters={true}
        className={(smallScreen && classes.rightsideGuttersSmall) || classes.rightsideGuttersLarge}
      >
        {navigationButton}
        <Typography variant="h6" color="inherit" className={classes.title} noWrap>
          <Link href="/">
            <img
              className={clsx(classes.logo, isNavigationOpen && smallScreen && classes.hide)}
              src={'/images/logo.svg'}
            />
          </Link>
        </Typography>

        {isDashboardPage ? dashboardMenu : null}
        {isDashboardPage ? lineInfo : userInfo}
        {languagePicker}

        {helpIcon}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
